import type { PatientGender } from '@dentexe/types';
import type {
  Patient,
  PatientContact,
  PatientMedicalCondition,
  PatientAllergy,
  PatientVital,
  VitalsHistoryResponse,
  ClinicalFlagTemplate,
  PatientClinicalFlag,
  Doctor,
} from '../../domain/types';

function toDateStrict(value: string): Date {
  return new Date(value);
}

function toPatientVitalDto(dto: {
  id: string;
  organizationId: string;
  clinicId: string;
  patientId: string;
  recordedAt: string;
  recordedBy: string | null;
  bpSystolic: number | null;
  bpDiastolic: number | null;
  pulseRate: number | null;
  weightKg: string | null;
  bloodSugarMgdl: number | null;
  smokingStatus: string | null;
  alcoholConsumption: string | null;
  diet: string | null;
  notes: string | null;
}): PatientVital {
  return {
    id: dto.id,
    organizationId: dto.organizationId,
    clinicId: dto.clinicId,
    patientId: dto.patientId,
    recordedAt: toDateStrict(dto.recordedAt),
    recordedBy: dto.recordedBy,
    bpSystolic: dto.bpSystolic,
    bpDiastolic: dto.bpDiastolic,
    pulseRate: dto.pulseRate,
    weightKg: dto.weightKg,
    bloodSugarMgdl: dto.bloodSugarMgdl,
    smokingStatus: dto.smokingStatus,
    alcoholConsumption: dto.alcoholConsumption,
    diet: dto.diet,
    notes: dto.notes,
  };
}

export function toPatient(raw: {
  id: string;
  organizationId: string;
  clinicId: string;
  firstName: string;
  lastName: string;
  name: string;
  dateOfBirth: string | null;
  age: number | null;
  gender: string;
  bloodGroup: string | null;
  occupation: string | null;
  patientDisplayId: string;
  isActive: boolean;
  deletedAt?: string | Date | null;
  medicalNotes: string | null;
  primaryDoctorId: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  phone?: string | null;
  email?: string | null;
  emergencyContactName?: string | null;
  emergencyContactRelation?: string | null;
  /** Wire name used by some list/detail payloads */
  contacts?: ContactWire | null;
  /** Singular name from PatientEntity props (GET /patients/:id) */
  contact?: EmbeddedContactWire | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- unwrap NestJS entity serialization { props: {...} }
  props?: any;
}): Patient {
  // Unwrap { props: {...} } from NestJS class-entity serialization
  const dto = (raw.props && typeof raw.props === 'object' ? raw.props : raw) as typeof raw;
  const embedded = dto.contacts ?? dto.contact ?? null;
  const contacts = embedded
    ? toPatientContact({
        id: embedded.id,
        organizationId: pickString(embedded, 'organizationId') ?? dto.organizationId,
        clinicId: pickString(embedded, 'clinicId') ?? dto.clinicId,
        patientId: pickString(embedded, 'patientId') ?? dto.id,
        primaryPhone: embedded.primaryPhone,
        alternatePhone: embedded.alternatePhone ?? null,
        email: embedded.email ?? null,
        addressLine1: embedded.addressLine1 ?? null,
        addressLine2: embedded.addressLine2 ?? null,
        city: embedded.city ?? null,
        state: embedded.state ?? null,
        nationality: embedded.nationality ?? null,
        countryId: embedded.countryId ?? null,
        stateId: embedded.stateId ?? null,
        districtId: embedded.districtId ?? null,
        pinCode: embedded.pinCode ?? null,
        emergencyContactName: embedded.emergencyContactName ?? null,
        emergencyContactRelation: embedded.emergencyContactRelation ?? null,
        emergencyContactPhone: embedded.emergencyContactPhone ?? null,
      })
    : undefined;

  const deletedAtRaw = dto.deletedAt ?? null;
  const deletedAt =
    deletedAtRaw instanceof Date
      ? deletedAtRaw
      : typeof deletedAtRaw === 'string' && deletedAtRaw
        ? toDateStrict(deletedAtRaw)
        : null;

  return {
    id: dto.id,
    organizationId: dto.organizationId,
    clinicId: dto.clinicId,
    firstName: dto.firstName,
    lastName: dto.lastName,
    name: dto.name,
    dateOfBirth: dto.dateOfBirth ? toDateStrict(dto.dateOfBirth) : null,
    age: dto.age ?? null,
    gender: dto.gender as PatientGender,
    bloodGroup: dto.bloodGroup,
    occupation: dto.occupation,
    patientDisplayId: dto.patientDisplayId,
    isActive: dto.isActive,
    deletedAt,
    medicalNotes: dto.medicalNotes,
    primaryDoctorId: dto.primaryDoctorId,
    createdBy: dto.createdBy,
    updatedBy: dto.updatedBy,
    createdAt: toDateStrict(dto.createdAt),
    updatedAt: toDateStrict(dto.updatedAt),
    contacts,
    phone: dto.phone ?? null,
    email: dto.email ?? null,
    emergencyContactName: dto.emergencyContactName ?? null,
    emergencyContactRelation: dto.emergencyContactRelation ?? null,
  };
}

type ContactWire = {
  id: string;
  organizationId: string;
  clinicId: string;
  patientId: string;
  primaryPhone: string;
  alternatePhone: string | null;
  email: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  nationality: string | null;
  countryId: string | null;
  stateId: string | null;
  districtId: string | null;
  pinCode: string | null;
  emergencyContactName: string | null;
  emergencyContactRelation: string | null;
  emergencyContactPhone: string | null;
};

/** Slimmer shape from PatientEntity.contact (ids filled from parent patient). */
type EmbeddedContactWire = {
  id: string;
  primaryPhone: string;
  alternatePhone?: string | null;
  email?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  nationality?: string | null;
  countryId?: string | null;
  stateId?: string | null;
  districtId?: string | null;
  pinCode?: string | null;
  emergencyContactName?: string | null;
  emergencyContactRelation?: string | null;
  emergencyContactPhone?: string | null;
};

function pickString(
  obj: ContactWire | EmbeddedContactWire,
  key: 'organizationId' | 'clinicId' | 'patientId',
): string | undefined {
  if (!(key in obj)) return undefined;
  const value = (obj as ContactWire)[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

export function toPatientContact(dto: {
  id: string;
  organizationId: string;
  clinicId: string;
  patientId: string;
  primaryPhone: string;
  alternatePhone: string | null;
  email: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  nationality: string | null;
  countryId: string | null;
  stateId: string | null;
  districtId: string | null;
  pinCode: string | null;
  emergencyContactName: string | null;
  emergencyContactRelation: string | null;
  emergencyContactPhone: string | null;
}): PatientContact {
  return {
    id: dto.id,
    organizationId: dto.organizationId,
    clinicId: dto.clinicId,
    patientId: dto.patientId,
    primaryPhone: dto.primaryPhone,
    alternatePhone: dto.alternatePhone,
    email: dto.email,
    addressLine1: dto.addressLine1,
    addressLine2: dto.addressLine2,
    city: dto.city,
    state: dto.state,
    nationality: dto.nationality,
    countryId: dto.countryId,
    stateId: dto.stateId,
    districtId: dto.districtId,
    pinCode: dto.pinCode,
    emergencyContactName: dto.emergencyContactName,
    emergencyContactRelation: dto.emergencyContactRelation,
    emergencyContactPhone: dto.emergencyContactPhone,
  };
}

export function toPatientMedicalCondition(dto: {
  id: string;
  organizationId: string;
  patientId: string;
  conditionId: string;
  notes: string | null;
  isActive: boolean;
}): PatientMedicalCondition {
  return { ...dto };
}

export function toPatientAllergy(dto: {
  id: string;
  organizationId: string;
  patientId: string;
  allergyId: string;
  reactionType: string | null;
  severity: string | null;
  notes: string | null;
  previousAnaestheticReaction: string | null;
  isActive: boolean;
}): PatientAllergy {
  return { ...dto };
}

export function toPatientVital(dto: {
  id: string;
  organizationId: string;
  clinicId: string;
  patientId: string;
  recordedAt: string;
  recordedBy: string | null;
  bpSystolic: number | null;
  bpDiastolic: number | null;
  pulseRate: number | null;
  weightKg: string | null;
  bloodSugarMgdl: number | null;
  smokingStatus: string | null;
  alcoholConsumption: string | null;
  diet: string | null;
  notes: string | null;
}): PatientVital {
  return toPatientVitalDto(dto);
}

export function toVitalsHistoryResponse(dto: {
  data: Array<{
    id: string;
    organizationId: string;
    clinicId: string;
    patientId: string;
    recordedAt: string;
    recordedBy: string | null;
    bpSystolic: number | null;
    bpDiastolic: number | null;
    pulseRate: number | null;
    weightKg: string | null;
    bloodSugarMgdl: number | null;
    smokingStatus: string | null;
    alcoholConsumption: string | null;
    diet: string | null;
    notes: string | null;
  }>;
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}): VitalsHistoryResponse {
  return {
    data: dto.data.map(toPatientVitalDto),
    total: dto.total,
    page: dto.page,
    limit: dto.limit,
    hasMore: dto.hasMore,
  };
}

export function toDoctor(dto: { id: string; displayName: string }): Doctor {
  return { id: dto.id, displayName: dto.displayName };
}

export function toClinicalFlagTemplate(dto: {
  id: string;
  name: string;
  icon: string | null;
  color: string | null;
  sortOrder: number;
}): ClinicalFlagTemplate {
  return { ...dto };
}

export function toPatientClinicalFlag(dto: {
  id: string;
  organizationId: string;
  patientId: string;
  flagId: string;
  value: string | null;
  notes: string | null;
  isActive: boolean;
  clinical_flag_templates?: {
    id: string;
    name: string;
    icon: string | null;
    color: string | null;
    sortOrder: number;
  } | null;
}): PatientClinicalFlag {
  return {
    id: dto.id,
    organizationId: dto.organizationId,
    patientId: dto.patientId,
    flagId: dto.flagId,
    value: dto.value,
    notes: dto.notes,
    isActive: dto.isActive,
    clinical_flag_templates: dto.clinical_flag_templates
      ? toClinicalFlagTemplate(dto.clinical_flag_templates)
      : undefined,
  };
}
